import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sword, 
  Coins, 
  Clock, 
  Skull, 
  Heart, 
  Gem, 
  Trophy, 
  MessageSquare,
  Target,
  Sparkles,
  Award,
  LucideIcon 
} from 'lucide-react';
import { Footer } from '../components/layout/Footer';
import { API_ENDPOINTS } from '../config/api';
import { SEO } from '../components/shared/SEO';
import { PageTransition } from '../components/ui/PageTransition';
import { AnimatedSection } from '../components/ui/AnimatedSection';

interface DonatorEntry {
  name: string;
  value: string;
}

interface LeaderboardEntry {
  name: string;
  value: string;
}

interface TopDonators {
  monthly: DonatorEntry[];
  alltime: DonatorEntry[];
  timestamp?: number;
}

interface TopVoters {
  current: LeaderboardEntry[];
  previous: LeaderboardEntry[];
  timestamp?: number;
}

interface SurvivalLeaderboard {
  weekly: LeaderboardEntry[];
  monthly: LeaderboardEntry[];
  alltime: LeaderboardEntry[];
  loading: boolean;
  error: string | null;
  timestamp?: number;
}

type LeaderboardType = 'weekly' | 'monthly' | 'alltime';
type VoterLeaderboardType = 'current' | 'previous';

export default function Leaderboard() {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState<'global' | 'survival' | 'tycoon'>('global');
  const [hasLoadedSurvival, setHasLoadedSurvival] = useState(false);
  const [hasLoadedTycoon, setHasLoadedTycoon] = useState(false);

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Global Leaderboard states
  const [donators, setDonators] = useState<TopDonators | null>(null);
  const [voters, setVoters] = useState<TopVoters | null>(null);
  const [loading, setLoading] = useState(true);
  const [votersLoading, setVotersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [votersError, setVotersError] = useState<string | null>(null);
  const [donatorActiveType, setDonatorActiveType] = useState<'monthly' | 'alltime'>('monthly');
  const [voterActiveType, setVoterActiveType] = useState<VoterLeaderboardType>('current');
  const [donatorExpanded, setDonatorExpanded] = useState(false);
  const [voterExpanded, setVoterExpanded] = useState(false);
  
  // Survival Mix Leaderboard states
  const [skillPower, setSkillPower] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [balance, setBalance] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [playtime, setPlaytime] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [playerKills, setPlayerKills] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [chatGameWins, setChatGameWins] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [meteorTreasure, setMeteorTreasure] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [hiddenTarget, setHiddenTarget] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  
  // Tycoon Leaderboard states
  const [tycoonCredits, setTycoonCredits] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [tycoonEthea, setTycoonEthea] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [tycoonPlaytime, setTycoonPlaytime] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [tycoonKills, setTycoonKills] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [tycoonSkillPower, setTycoonSkillPower] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [tycoonMeteorTreasure, setTycoonMeteorTreasure] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [tycoonChatGameWins, setTycoonChatGameWins] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  const [tycoonHiddenTarget, setTycoonHiddenTarget] = useState<SurvivalLeaderboard>({ 
    weekly: [], monthly: [], alltime: [], loading: true, error: null 
  });
  
  const [skillPowerType, setSkillPowerType] = useState<LeaderboardType>('weekly');
  const [balanceType, setBalanceType] = useState<LeaderboardType>('weekly');
  const [playtimeType, setPlaytimeType] = useState<LeaderboardType>('weekly');
  const [playerKillsType, setPlayerKillsType] = useState<LeaderboardType>('weekly');
  const [chatGameWinsType, setChatGameWinsType] = useState<LeaderboardType>('weekly');
  const [meteorTreasureType, setMeteorTreasureType] = useState<LeaderboardType>('weekly');
  const [hiddenTargetType, setHiddenTargetType] = useState<LeaderboardType>('weekly');
  
  // Tycoon active types
  const [tycoonCreditsType, setTycoonCreditsType] = useState<LeaderboardType>('weekly');
  const [tycoonEtheaType, setTycoonEtheaType] = useState<LeaderboardType>('weekly');
  const [tycoonPlaytimeType, setTycoonPlaytimeType] = useState<LeaderboardType>('weekly');
  const [tycoonKillsType, setTycoonKillsType] = useState<LeaderboardType>('weekly');
  const [tycoonSkillPowerType, setTycoonSkillPowerType] = useState<LeaderboardType>('weekly');
  const [tycoonMeteorTreasureType, setTycoonMeteorTreasureType] = useState<LeaderboardType>('weekly');
  const [tycoonChatGameWinsType, setTycoonChatGameWinsType] = useState<LeaderboardType>('weekly');
  const [tycoonHiddenTargetType, setTycoonHiddenTargetType] = useState<LeaderboardType>('weekly');

  // Add expanded states for each leaderboard
  const [skillPowerExpanded, setSkillPowerExpanded] = useState(false);
  const [balanceExpanded, setBalanceExpanded] = useState(false);
  const [playtimeExpanded, setPlaytimeExpanded] = useState(false);
  const [playerKillsExpanded, setPlayerKillsExpanded] = useState(false);
  const [chatGameWinsExpanded, setChatGameWinsExpanded] = useState(false);
  const [meteorTreasureExpanded, setMeteorTreasureExpanded] = useState(false);
  const [hiddenTargetExpanded, setHiddenTargetExpanded] = useState(false);
  const [tycoonCreditsExpanded, setTycoonCreditsExpanded] = useState(false);
  const [tycoonEtheaExpanded, setTycoonEtheaExpanded] = useState(false);
  const [tycoonPlaytimeExpanded, setTycoonPlaytimeExpanded] = useState(false);
  const [tycoonKillsExpanded, setTycoonKillsExpanded] = useState(false);
  const [tycoonSkillPowerExpanded, setTycoonSkillPowerExpanded] = useState(false);
  const [tycoonMeteorTreasureExpanded, setTycoonMeteorTreasureExpanded] = useState(false);
  const [tycoonChatGameWinsExpanded, setTycoonChatGameWinsExpanded] = useState(false);
  const [tycoonHiddenTargetExpanded, setTycoonHiddenTargetExpanded] = useState(false);

  // Navigation functions
  const goToNext = () => {
    if (activeSection === 'tycoon') setActiveSection('global');
    else if (activeSection === 'global') setActiveSection('survival');
    else if (activeSection === 'survival') setActiveSection('tycoon');
  };

  const goToPrev = () => {
    if (activeSection === 'tycoon') setActiveSection('survival');
    else if (activeSection === 'global') setActiveSection('tycoon');
    else if (activeSection === 'survival') setActiveSection('global');
  };

  // Global leaderboard fetch
  useEffect(() => {
    if (activeSection !== 'global') return;

    const fetchDonators = async () => {
      try {
        const response = await axios.get<TopDonators>(API_ENDPOINTS.leaderboards.global);
        setDonators(response.data);
        setError(null);
      } catch (err) {
        setError('Failed to fetch leaderboard data');
        console.error('Error fetching leaderboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDonators();
    const interval = setInterval(fetchDonators, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [activeSection]);
  
  // Top Voters fetch
  useEffect(() => {
    if (activeSection !== 'global') return;

    const fetchVoters = async () => {
      try {
        const response = await axios.get<TopVoters>(API_ENDPOINTS.leaderboards.voters);
        setVoters(response.data);
        setVotersError(null);
      } catch (err) {
        setVotersError('Failed to fetch voters data');
        console.error('Error fetching voters data:', err);
      } finally {
        setVotersLoading(false);
      }
    };

    fetchVoters();
    const interval = setInterval(fetchVoters, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [activeSection]);

  // Survival leaderboard fetch
  useEffect(() => {
    if (activeSection !== 'survival' || hasLoadedSurvival) return;

    const fetchSurvivalLeaderboards = async () => {
      try {
        const response = await axios.get<Record<string, SurvivalLeaderboard>>(API_ENDPOINTS.leaderboards.survival);
        setSkillPower({ ...response.data['aureliumskills_power'], loading: false, error: null });
        setBalance({ ...response.data['vault_eco_balance'], loading: false, error: null });
        setPlaytime({ ...response.data['statistic_time_played'], loading: false, error: null });
        setPlayerKills({ ...response.data['statistic_player_kills'], loading: false, error: null });
        setChatGameWins({ ...response.data['kixschatgames_wins'], loading: false, error: null });
        setMeteorTreasure({ ...response.data['skquest_meteortreasurefound'], loading: false, error: null });
        setHiddenTarget({ ...response.data['skquest_hiddentargetfound'], loading: false, error: null });
        setHasLoadedSurvival(true);
      } catch (err) {
        const fallbackData = Array(10).fill({ name: 'Notch', value: '0' });
        const fallbackState = {
          weekly: fallbackData,
          monthly: fallbackData,
          alltime: fallbackData,
          loading: false,
          error: null
        };
        setSkillPower(fallbackState);
        setBalance(fallbackState);
        setPlaytime(fallbackState);
        setPlayerKills(fallbackState);
        setChatGameWins(fallbackState);
        setMeteorTreasure(fallbackState);
        setHiddenTarget(fallbackState);
        setHasLoadedSurvival(true);
      }
    };

    fetchSurvivalLeaderboards();
  }, [activeSection, hasLoadedSurvival]);

  // Tycoon leaderboard fetch
  useEffect(() => {
    if (activeSection !== 'tycoon' || hasLoadedTycoon) return;

    const fetchTycoonLeaderboards = async () => {
      // Set loading state
      const loadingState = {
        weekly: [],
        monthly: [],
        alltime: [],
        loading: true,
        error: null
      };
      setTycoonCredits(loadingState);
      setTycoonEthea(loadingState);
      setTycoonPlaytime(loadingState);
      setTycoonKills(loadingState);
      setTycoonSkillPower(loadingState);
      setTycoonMeteorTreasure(loadingState);
      setTycoonChatGameWins(loadingState);
      setTycoonHiddenTarget(loadingState);
      try {
        const response = await axios.get<Record<string, SurvivalLeaderboard>>(API_ENDPOINTS.leaderboards.tycoon);
        
        // Check if we have the expected data structure
        const requiredEndpoints = [
          'vault_eco_balance',
          'gemseconomy_balance_default',
          'statistic_time_played',
          'statistic_player_kills',
          'ecoskills_total_skill_level',
          'skmeteor_found',
          'hiddentarget_found'
        ];

        const missingEndpoints = requiredEndpoints.filter(endpoint => !response.data[endpoint]);
        if (missingEndpoints.length > 0) {
          throw new Error(`Missing data for endpoints: ${missingEndpoints.join(', ')}`);
        }

        setTycoonCredits({
          ...response.data['vault_eco_balance'],
          loading: false,
          error: null
        });
        setTycoonEthea({
          ...response.data['gemseconomy_balance_default'],
          loading: false,
          error: null
        });
        setTycoonPlaytime({
          ...response.data['statistic_time_played'],
          loading: false,
          error: null
        });
        setTycoonKills({
          ...response.data['statistic_player_kills'],
          loading: false,
          error: null
        });
        setTycoonSkillPower({
          ...response.data['ecoskills_total_skill_level'],
          loading: false,
          error: null
        });
        setTycoonMeteorTreasure({
          ...response.data['skmeteor_found'],
          loading: false,
          error: null
        });
        setTycoonChatGameWins({
          ...response.data['kixschatgames_wins'],
          loading: false,
          error: null
        });
        setTycoonHiddenTarget({
          ...response.data['hiddentarget_found'],
          loading: false,
          error: null
        });
        setHasLoadedTycoon(true);
      } catch (err) {
        console.error('Error fetching tycoon leaderboards:', err);
        const error = err as Error;
        
        const fallbackData = Array(10).fill({ name: 'Notch', value: '0' });
        const fallbackState = {
          weekly: fallbackData,
          monthly: fallbackData,
          alltime: fallbackData,
          loading: false,
          error: error.message || 'Failed to fetch tycoon leaderboards'
        };

        setTycoonCredits(fallbackState);
        setTycoonEthea(fallbackState);
        setTycoonPlaytime(fallbackState);
        setTycoonKills(fallbackState);
        setTycoonSkillPower(fallbackState);
        setTycoonMeteorTreasure(fallbackState);
        setTycoonHiddenTarget(fallbackState);
        setTycoonChatGameWins(fallbackState);
        setHasLoadedTycoon(true);
      }
    };

    fetchTycoonLeaderboards();
  }, [activeSection, hasLoadedTycoon]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center text-red-500">
        <div className="text-center">
          <p className="text-xl font-semibold mb-2">{error}</p>
          <p className="text-gray-400">Please try again later</p>
        </div>
      </div>
    );
  }

  const currentData = donatorActiveType === 'monthly' ? donators?.monthly : donators?.alltime;
  const maxEntries = Math.max(donators?.monthly.length ?? 0, donators?.alltime.length ?? 0);

  const LeaderboardCard = ({ 
    title, 
    data, 
    loading, 
    error,
    activeType,
    onTypeChange,
    isExpanded,
    onExpandChange
  }: { 
    title: string; 
    data: SurvivalLeaderboard; 
    loading: boolean; 
    error: string | null;
    activeType: LeaderboardType;
    onTypeChange: (type: LeaderboardType) => void;
    isExpanded: boolean;
    onExpandChange: (expanded: boolean) => void;
  }) => {
    const currentData = data[activeType];
    const { t } = useTranslation();

    const displayedEntries = isExpanded ? currentData.slice(0, 10) : currentData.slice(0, 5);
    const hasMoreEntries = currentData.length > 5;

    // Animation variants for staggered animations
    const containerVariants = {
      hidden: { 
        opacity: 0 
      },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05,
          delayChildren: 0.1
        }
      },
      exit: { 
        opacity: 0,
        transition: {
          staggerChildren: 0.03,
          staggerDirection: -1
        }
      }
    };

    const itemVariants = {
      hidden: { 
        opacity: 0, 
        y: 10 
      },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: {
          type: "spring",
          stiffness: 100,
          damping: 15
        }
      },
      exit: { 
        opacity: 0, 
        y: -10,
        transition: {
          duration: 0.2
        }
      }
    };

    const getIcon = () => {
      switch (title) {
        case 'Top Credits':
          return Coins;
        case 'Top Ethea':
          return Gem;
        case 'Top Balance':
          return Coins;
        case t('leaderboard.topDonator'):
          return Heart;
        case 'Top Skill Power':
          return Sword;
        case 'Top Playtime':
          return Clock;
        case 'Top Player Kills':
          return Skull;
        case 'Top Chat Game Wins':
          return MessageSquare;
        case 'Top Meteor Treasure':
          return Sparkles;
        case 'Top Hidden Target':
          return Target;
        default:
          return Trophy;
      }
    };

    const Icon = getIcon();

    const formatValue = (value: string) => {
      switch (title) {
        case 'Top Credits':
          return `◆${value.split('.')[0]}`;
        case 'Top Ethea':
          return `₻${value}`;
        case 'Top Balance':
          return `$${value.split(',')[0]}`;
        case t('leaderboard.topDonator'):
          return `Rp${value}`;
        default:
          return value;
      }
    };

    return (
      <div className="bg-gradient-to-br from-gray-800/90 via-gray-800/70 to-gray-800/90 backdrop-blur-sm rounded-xl p-6 h-full border border-white/10 hover:border-primary-500/50 transition-all duration-300 relative overflow-hidden">
        <div className="relative">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Icon className="w-6 h-6 text-primary-400 group-hover:text-primary-300 transition-colors duration-300" />
            </div>
            <h3 className="text-2xl font-bold text-center bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">{title}</h3>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-8">{error}</div>
          ) : (
            <>
              <AnimatePresence mode="wait" key={activeType}>
                <motion.div 
                  className="space-y-3"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  layout
                >
                  {displayedEntries.map((entry, index) => (
                    <motion.div 
                      key={entry.name}
                      variants={itemVariants}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-300 hover:translate-x-1 ${
                        index === 0 
                          ? 'bg-gradient-to-r from-yellow-500/10 to-yellow-600/5 border-yellow-500/20 hover:border-yellow-500/60' 
                          : index === 1
                          ? 'bg-gradient-to-r from-gray-300/10 to-gray-400/5 border-gray-400/20 hover:border-gray-400/40'
                          : index === 2
                          ? 'bg-gradient-to-r from-yellow-600/10 to-yellow-700/5 border-yellow-600/20 hover:border-yellow-600/40'
                          : 'bg-gray-800/30 backdrop-blur-sm border-gray-600/30 hover:bg-gray-800/40 hover:border-gray-500/60'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`w-6 text-center font-bold flex-shrink-0 ${
                          index === 0 ? 'text-yellow-400 text-lg' :
                          index === 1 ? 'text-gray-300 text-lg' :
                          index === 2 ? 'text-yellow-600 text-lg' :
                          'text-gray-400'
                        }`}>{index + 1}</span>
                        <div className="relative">
                          <img
                            src={`https://mc-heads.net/avatar/${entry.name}/40`}
                            alt={`${entry.name}'s avatar`}
                            className="w-7 h-7 rounded-md flex-shrink-0 transition-transform duration-300 hover:scale-110"
                            loading="lazy"
                          />
                          {index < 3 && (
                            <div className={`absolute -inset-1 rounded-md ${
                              index === 0 ? 'bg-yellow-400/20' :
                              index === 1 ? 'bg-gray-300/20' :
                              'bg-yellow-600/20'
                            } blur-sm -z-10`}></div>
                          )}
                        </div>
                        <span className="font-medium" style={{ 
                          wordBreak: 'break-all',
                          overflowWrap: 'break-word',
                          maxWidth: '40%'
                        }}>{entry.name}</span>
                      </div>
                      <span className="font-semibold flex-shrink-0 text-right text-white" style={{ 
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                        maxWidth: '40%'
                      }}>{formatValue(entry.value)}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </AnimatePresence>

              {hasMoreEntries && (
                <motion.button
                  onClick={() => onExpandChange(!isExpanded)}
                  className="w-full mt-4 py-2 px-4 rounded-lg text-sm font-medium transition-all bg-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-600/50 backdrop-blur-sm border border-gray-600/30 hover:border-gray-500/60 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isExpanded ? (
                    <>
                      {t('leaderboard.showLess')}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      {t('leaderboard.showMore')}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </motion.button>
              )}

              {data.timestamp && (
                <div className="mt-4 mb-2 text-center text-sm text-gray-400">
                  {t('leaderboard.lastUpdated', { time: formatTimestamp(data.timestamp) })}
                </div>
              )}

              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {title !== t('leaderboard.topDonator') && (
                  <motion.button
                    onClick={() => onTypeChange('weekly')}
                    className={`py-2 px-4 sm:py-2.5 sm:px-6 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                      activeType === 'weekly'
                        ? 'bg-primary-500/20 text-white ring-2 ring-primary-500'
                        : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-600/50 backdrop-blur-sm'
                    }`}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {t('leaderboard.weeklyShort')}
                  </motion.button>
                )}
                <motion.button
                  onClick={() => onTypeChange('monthly')}
                  className={`py-2 px-4 sm:py-2.5 sm:px-6 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    activeType === 'monthly'
                      ? 'bg-primary-500/20 text-white ring-2 ring-primary-500'
                      : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-600/50 backdrop-blur-sm'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t('leaderboard.monthlyShort')}
                </motion.button>
                <motion.button
                  onClick={() => onTypeChange('alltime')}
                  className={`py-2 px-4 sm:py-2.5 sm:px-6 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    activeType === 'alltime'
                      ? 'bg-primary-500/20 text-white ring-2 ring-primary-500'
                      : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-600/50 backdrop-blur-sm'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t('leaderboard.alltimeShort')}
                </motion.button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const VoterLeaderboardCard = ({ 
    title, 
    data, 
    loading, 
    error,
    activeType,
    onTypeChange,
    isExpanded,
    onExpandChange
  }: { 
    title: string; 
    data: TopVoters | null; 
    loading: boolean; 
    error: string | null;
    activeType: VoterLeaderboardType;
    onTypeChange: (type: VoterLeaderboardType) => void;
    isExpanded: boolean;
    onExpandChange: (expanded: boolean) => void;
  }) => {
    if (!data) return null;
    
    const currentData = data[activeType];
    const { t } = useTranslation();

    const displayedEntries = isExpanded ? currentData.slice(0, 50) : currentData.slice(0, 5);
    const hasMoreEntries = currentData.length > 5;

    // Animation variants for staggered animations
    const containerVariants = {
      hidden: { 
        opacity: 0 
      },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: 0.05,
          delayChildren: 0.1
        }
      },
      exit: { 
        opacity: 0,
        transition: {
          staggerChildren: 0.03,
          staggerDirection: -1
        }
      }
    };

    const itemVariants = {
      hidden: { 
        opacity: 0, 
        y: 10 
      },
      visible: { 
        opacity: 1, 
        y: 0,
        transition: {
          type: "spring",
          stiffness: 100,
          damping: 15
        }
      },
      exit: { 
        opacity: 0, 
        y: -10,
        transition: {
          duration: 0.2
        }
      }
    };

    return (
      <div className="bg-gradient-to-br from-gray-800/90 via-gray-800/70 to-gray-800/90 backdrop-blur-sm rounded-xl p-6 h-full border border-white/10 hover:border-primary-500/50 transition-all duration-300 relative overflow-hidden">
        <div className="relative">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500/20 to-primary-500/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
              <Award className="w-6 h-6 text-primary-400 group-hover:text-primary-300 transition-colors duration-300" />
            </div>
            <h3 className="text-2xl font-bold text-center bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">{title}</h3>
          </div>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent"></div>
            </div>
          ) : error ? (
            <div className="text-red-500 text-center py-8">{error}</div>
          ) : (
            <>
              <AnimatePresence mode="wait" key={activeType}>
                <motion.div 
                  className="space-y-3"
                  variants={containerVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  layout
                >
                  {displayedEntries.map((entry, index) => (
                    <motion.div 
                      key={entry.name}
                      variants={itemVariants}
                      className={`flex items-center justify-between p-3 rounded-lg border transition-all duration-300 hover:translate-x-1 ${
                        index === 0 
                          ? 'bg-gradient-to-r from-yellow-500/10 to-yellow-600/5 border-yellow-500/20 hover:border-yellow-500/60' 
                          : index === 1
                          ? 'bg-gradient-to-r from-gray-300/10 to-gray-400/5 border-gray-400/20 hover:border-gray-400/40'
                          : index === 2
                          ? 'bg-gradient-to-r from-yellow-600/10 to-yellow-700/5 border-yellow-600/20 hover:border-yellow-600/40'
                          : 'bg-gray-800/30 backdrop-blur-sm border-gray-600/30 hover:bg-gray-800/40 hover:border-gray-500/60'
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <span className={`w-6 text-center font-bold flex-shrink-0 ${
                          index === 0 ? 'text-yellow-400 text-lg' :
                          index === 1 ? 'text-gray-300 text-lg' :
                          index === 2 ? 'text-yellow-600 text-lg' :
                          'text-gray-400'
                        }`}>{index + 1}</span>
                        <div className="relative">
                          <img
                            src={`https://mc-heads.net/avatar/${entry.name}/40`}
                            alt={`${entry.name}'s avatar`}
                            className="w-7 h-7 rounded-md flex-shrink-0 transition-transform duration-300 hover:scale-110"
                            loading="lazy"
                          />
                          {index < 3 && (
                            <div className={`absolute -inset-1 rounded-md ${
                              index === 0 ? 'bg-yellow-400/20' :
                              index === 1 ? 'bg-gray-300/20' :
                              'bg-yellow-600/20'
                            } blur-sm -z-10`}></div>
                          )}
                        </div>
                        <span className="font-medium" style={{ 
                          wordBreak: 'break-all',
                          overflowWrap: 'break-word',
                          maxWidth: '40%'
                        }}>{entry.name}</span>
                      </div>
                      <span className="font-semibold flex-shrink-0 text-right text-white" style={{ 
                        wordBreak: 'break-all',
                        overflowWrap: 'break-word',
                        maxWidth: '40%'
                      }}>{entry.value}</span>
                    </motion.div>
                  ))}
                </motion.div>
              </AnimatePresence>

              {hasMoreEntries && (
                <motion.button
                  onClick={() => onExpandChange(!isExpanded)}
                  className="w-full mt-4 py-2 px-4 rounded-lg text-sm font-medium transition-all bg-gray-700/50 text-gray-300 hover:text-white hover:bg-gray-600/50 backdrop-blur-sm border border-gray-600/30 hover:border-gray-500/60 flex items-center justify-center gap-2"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isExpanded ? (
                    <>
                      {t('leaderboard.showLess')}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </>
                  ) : (
                    <>
                      {t('leaderboard.showMore')}
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </motion.button>
              )}

              {data.timestamp && (
                <div className="mt-4 mb-2 text-center text-sm text-gray-400">
                  {t('leaderboard.lastUpdated', { time: formatTimestamp(data.timestamp) })}
                </div>
              )}

              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <motion.button
                  onClick={() => onTypeChange('current')}
                  className={`py-2 px-4 sm:py-2.5 sm:px-6 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    activeType === 'current'
                      ? 'bg-primary-500/20 text-white ring-2 ring-primary-500'
                      : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-600/50 backdrop-blur-sm'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t('leaderboard.currentShort')}
                </motion.button>
                <motion.button
                  onClick={() => onTypeChange('previous')}
                  className={`py-2 px-4 sm:py-2.5 sm:px-6 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                    activeType === 'previous'
                      ? 'bg-primary-500/20 text-white ring-2 ring-primary-500'
                      : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-600/50 backdrop-blur-sm'
                  }`}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {t('leaderboard.previousShort')}
                </motion.button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageTransition>
      <SEO 
        title={t('seo.leaderboard.title')}
        description={t('seo.leaderboard.description')}
      />
      <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        {/* Hero Section with Gradient Background */}
        <div className="relative mb-16">
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/70 via-gray-900/50 to-gray-900/90" />
          <div className="relative">
            <AnimatedSection animation="fade-down" className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4">
                <span className="bg-gradient-to-r from-primary-400 to-yellow-300 text-transparent bg-clip-text">
                  {t('leaderboard.title')}
                </span>
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                {t('leaderboard.description')}
              </p>
            </AnimatedSection>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          {/* Navigation Buttons */}
          <AnimatedSection animation="fade-up" className="mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
              <motion.button
                onClick={goToPrev}
                className="flex items-center gap-2 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 px-4 py-2 rounded-lg text-base sm:text-lg font-medium transition-all group w-full sm:w-auto justify-center border border-white/5 hover:border-white/10"
                whileHover={{ x: -5 }}
                whileTap={{ scale: 0.95 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 transition-transform group-hover:-translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                {t('leaderboard.previous')}
              </motion.button>
              <div className="flex gap-4 sm:gap-8 items-center">
                <motion.button
                  onClick={() => setActiveSection('tycoon')}
                  className={`text-xl sm:text-2xl font-bold transition-all ${
                    activeSection === 'tycoon' 
                      ? 'text-primary scale-110 border-b-2 border-primary pb-1'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                  whileHover={{ scale: activeSection === 'tycoon' ? 1.1 : 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Tycoon
                </motion.button>
                <motion.button
                  onClick={() => setActiveSection('global')}
                  className={`text-xl sm:text-2xl font-bold transition-all ${
                    activeSection === 'global'
                      ? 'text-primary scale-110 border-b-2 border-primary pb-1'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                  whileHover={{ scale: activeSection === 'global' ? 1.1 : 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Global
                </motion.button>
                <motion.button
                  onClick={() => setActiveSection('survival')}
                  className={`text-xl sm:text-2xl font-bold transition-all ${
                    activeSection === 'survival'
                      ? 'text-primary scale-110 border-b-2 border-primary pb-1'
                      : 'text-gray-400 hover:text-gray-200'
                  }`}
                  whileHover={{ scale: activeSection === 'survival' ? 1.1 : 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Survival Mix
                </motion.button>
              </div>
              <motion.button
                onClick={goToNext}
                className="flex items-center gap-2 bg-gray-800/80 backdrop-blur-sm hover:bg-gray-700/80 px-4 py-2 rounded-lg text-base sm:text-lg font-medium transition-all group w-full sm:w-auto justify-center border border-white/5 hover:border-white/10"
                whileHover={{ x: 5 }}
                whileTap={{ scale: 0.95 }}
              >
                {t('leaderboard.next')}
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 transition-transform group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </motion.button>
            </div>
          </AnimatedSection>

          {/* Sections Container */}
          <div>
            <AnimatePresence mode="wait">
              {activeSection === 'tycoon' && (
                <motion.div
                  key="tycoon"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    duration: 0.4, 
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                  }}
                >
                  {/* Tycoon Leaderboard */}
                  <div className="max-w-7xl mx-auto px-2 sm:px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Credits"
                          data={tycoonCredits}
                          loading={tycoonCredits.loading}
                          error={tycoonCredits.error}
                          activeType={tycoonCreditsType}
                          onTypeChange={setTycoonCreditsType}
                          isExpanded={tycoonCreditsExpanded}
                          onExpandChange={setTycoonCreditsExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Ethea"
                          data={tycoonEthea}
                          loading={tycoonEthea.loading}
                          error={tycoonEthea.error}
                          activeType={tycoonEtheaType}
                          onTypeChange={setTycoonEtheaType}
                          isExpanded={tycoonEtheaExpanded}
                          onExpandChange={setTycoonEtheaExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Playtime"
                          data={tycoonPlaytime}
                          loading={tycoonPlaytime.loading}
                          error={tycoonPlaytime.error}
                          activeType={tycoonPlaytimeType}
                          onTypeChange={setTycoonPlaytimeType}
                          isExpanded={tycoonPlaytimeExpanded}
                          onExpandChange={setTycoonPlaytimeExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Player Kills"
                          data={tycoonKills}
                          loading={tycoonKills.loading}
                          error={tycoonKills.error}
                          activeType={tycoonKillsType}
                          onTypeChange={setTycoonKillsType}
                          isExpanded={tycoonKillsExpanded}
                          onExpandChange={setTycoonKillsExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Skill Power"
                          data={tycoonSkillPower}
                          loading={tycoonSkillPower.loading}
                          error={tycoonSkillPower.error}
                          activeType={tycoonSkillPowerType}
                          onTypeChange={setTycoonSkillPowerType}
                          isExpanded={tycoonSkillPowerExpanded}
                          onExpandChange={setTycoonSkillPowerExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Meteor Treasure"
                          data={tycoonMeteorTreasure}
                          loading={tycoonMeteorTreasure.loading}
                          error={tycoonMeteorTreasure.error}
                          activeType={tycoonMeteorTreasureType}
                          onTypeChange={setTycoonMeteorTreasureType}
                          isExpanded={tycoonMeteorTreasureExpanded}
                          onExpandChange={setTycoonMeteorTreasureExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Chat Game Wins"
                          data={tycoonChatGameWins}
                          loading={tycoonChatGameWins.loading}
                          error={tycoonChatGameWins.error}
                          activeType={tycoonChatGameWinsType}
                          onTypeChange={setTycoonChatGameWinsType}
                          isExpanded={tycoonChatGameWinsExpanded}
                          onExpandChange={setTycoonChatGameWinsExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Hidden Target"
                          data={tycoonHiddenTarget}
                          loading={tycoonHiddenTarget.loading}
                          error={tycoonHiddenTarget.error}
                          activeType={tycoonHiddenTargetType}
                          onTypeChange={setTycoonHiddenTargetType}
                          isExpanded={tycoonHiddenTargetExpanded}
                          onExpandChange={setTycoonHiddenTargetExpanded}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeSection === 'global' && (
                <motion.div
                  key="global"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    duration: 0.4, 
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                  }}
                >
                  {/* Global Leaderboard */}
                  <div className="max-w-7xl mx-auto px-2 sm:px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 place-items-center">
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title={t('leaderboard.topDonator')}
                          data={{
                            weekly: [],
                            monthly: donators?.monthly || [],
                            alltime: donators?.alltime || [],
                            loading: loading,
                            error: error,
                            timestamp: donators?.timestamp
                          }}
                          loading={loading}
                          error={error}
                          activeType={donatorActiveType === 'monthly' ? 'monthly' : 'alltime'}
                          onTypeChange={(type) => setDonatorActiveType(type === 'monthly' ? 'monthly' : 'alltime')}
                          isExpanded={donatorExpanded}
                          onExpandChange={setDonatorExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <VoterLeaderboardCard
                          title={t('leaderboard.topVoters')}
                          data={voters}
                          loading={votersLoading}
                          error={votersError}
                          activeType={voterActiveType}
                          onTypeChange={setVoterActiveType}
                          isExpanded={voterExpanded}
                          onExpandChange={setVoterExpanded}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeSection === 'survival' && (
                <motion.div
                  key="survival"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    duration: 0.4, 
                    type: "spring",
                    stiffness: 100,
                    damping: 15
                  }}
                >
                  {/* Survival Mix Leaderboard */}
                  <div className="max-w-7xl mx-auto px-2 sm:px-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Skill Power"
                          data={skillPower}
                          loading={skillPower.loading}
                          error={skillPower.error}
                          activeType={skillPowerType}
                          onTypeChange={setSkillPowerType}
                          isExpanded={skillPowerExpanded}
                          onExpandChange={setSkillPowerExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Balance"
                          data={balance}
                          loading={balance.loading}
                          error={balance.error}
                          activeType={balanceType}
                          onTypeChange={setBalanceType}
                          isExpanded={balanceExpanded}
                          onExpandChange={setBalanceExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Playtime"
                          data={playtime}
                          loading={playtime.loading}
                          error={playtime.error}
                          activeType={playtimeType}
                          onTypeChange={setPlaytimeType}
                          isExpanded={playtimeExpanded}
                          onExpandChange={setPlaytimeExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Player Kills"
                          data={playerKills}
                          loading={playerKills.loading}
                          error={playerKills.error}
                          activeType={playerKillsType}
                          onTypeChange={setPlayerKillsType}
                          isExpanded={playerKillsExpanded}
                          onExpandChange={setPlayerKillsExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Chat Game Wins"
                          data={chatGameWins}
                          loading={chatGameWins.loading}
                          error={chatGameWins.error}
                          activeType={chatGameWinsType}
                          onTypeChange={setChatGameWinsType}
                          isExpanded={chatGameWinsExpanded}
                          onExpandChange={setChatGameWinsExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full">
                        <LeaderboardCard
                          title="Top Meteor Treasure"
                          data={meteorTreasure}
                          loading={meteorTreasure.loading}
                          error={meteorTreasure.error}
                          activeType={meteorTreasureType}
                          onTypeChange={setMeteorTreasureType}
                          isExpanded={meteorTreasureExpanded}
                          onExpandChange={setMeteorTreasureExpanded}
                        />
                      </div>
                      <div className="max-w-xl mx-auto w-full md:col-span-2">
                        <LeaderboardCard
                          title="Top Hidden Target"
                          data={hiddenTarget}
                          loading={hiddenTarget.loading}
                          error={hiddenTarget.error}
                          activeType={hiddenTargetType}
                          onTypeChange={setHiddenTargetType}
                          isExpanded={hiddenTargetExpanded}
                          onExpandChange={setHiddenTargetExpanded}
                        />
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
      <Footer />
    </PageTransition>
  );
} 